// @givmo/board — an officer's to-do list, on every Board page in the console (CBG-UX7-WEB-OFF; design
// stop design-268's §4.1, §4.2, §5, §6.2 and §7, frames o1–o4).
//
// The console's Board mount renders it ONCE, beside Board's pages and inside Board's officer host
// (`BoardOfficerToDo`), so every Board page has it and nothing else does: never the console's Settings
// page, not even its Board section (§7), which draws Board's cards through the same host. It reads one
// officer read, `GET …/to-do`, whose every kind the backend composes only for a holder of its act's
// string; the capacity rail holds (an officer surface calls officer operations only).
//
// KEPT CURRENT (§6.3). The read sits under one key root, `['board-todo', orgRef]`, and every officer
// act settles it, as the proposals' acts settle theirs (`useSettleProposals`). Every officer act on
// Board's pages runs as a mutation of the console's one QueryClient, so the list settles on each one's
// success itself — a write that adds a step, sends minutes or approves a record re-reads the list once
// it lands, and an act a later row adds needs no line here to stay honest. It also reads again when the
// window regains focus; nothing polls.
//
// ONLY FOR SOMEONE IT CAN LIST SOMETHING FOR (CBG-UX7-QUIET). The read lists each kind only for a
// holder of its act's string, so a reader who holds none of them (`board:read` alone: an auditor,
// staff) would only ever see a tab saying there is nothing to do. The list isn't drawn for them, and
// nothing is read (review-274's P1; Jonathan's word, 2026-10-24 11:11 PDT: hide the tab).
//
// AN OFFICER WHO IS ALSO ON THE BOARD (Jonathan is both, as most small boards' secretaries and chairs
// are) keeps two lists, each where its acts are made (D-CBG-35 (3)): this one, and the director's on
// their board page, where their signature's own sign-in rules live. One quiet line at the list's foot
// says so, with the way there, read from the roster this console already serves: a board seat whose
// account is the person's own.

import { useEffect, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { BOARD_DIRECTOR_PAGES, BOARD_READ_PERMISSION } from '../../board-routes';
import { hasBoardPermission, useBoardHost } from '../../board-host';
import { Link, usePathname } from '../../board-routing';
import { useBoardOperations } from '../../board-runtime';
import { extractBoardMembers, isCommitteeOnlySeat } from '../roster/roster-decode';
import { identitiesOf, officerList, type ToDoList } from './todo-model';
import { ToDoPanel } from './todo-panel';
import { useToDoPresence, useToDoStore } from './todo-store';
import { todoWords as w } from './todo-words';

/** The strings the officer's list is composed for, one per kind's act (backend `app/domain/
 *  board_todo.py`'s `OFFICER_KIND_CAPABILITIES`): the list is drawn for a holder of any of them. */
export const OFFICER_TODO_PERMISSIONS = [
  'board_record:write',
  'board_record:approve',
  'board_record:import',
  'board_member:manage',
  'board_member:grant_access',
  'board_conflict_disclosure:manage',
] as const;

/** The officer's list's one key root (§6.3). */
export function boardToDoKey(orgRef: string | null) {
  return ['board-todo', orgRef ?? ''] as const;
}

export function BoardOfficerToDo() {
  const { orgRef, isReady, permissions, userId } = useBoardHost();
  if (
    orgRef === null ||
    !isReady ||
    !hasBoardPermission(permissions, BOARD_READ_PERMISSION) ||
    !OFFICER_TODO_PERMISSIONS.some((permission) => hasBoardPermission(permissions, permission))
  )
    return null;
  return <OfficerToDoForOrg orgRef={orgRef} account={userId ?? null} />;
}

function OfficerToDoForOrg({ orgRef, account }: { orgRef: string; account: string | null }) {
  useToDoPresence('officer');
  const operations = useBoardOperations();
  const queryClient = useQueryClient();
  const store = useToDoStore();

  const toDoQuery = useQuery({
    queryKey: boardToDoKey(orgRef),
    retry: false,
    refetchOnWindowFocus: 'always',
    queryFn: async (): Promise<ToDoList> => {
      const { data } = await operations.officerGetBoardToDo({
        path: { org_ref: orgRef },
        throwOnError: true,
      });
      return officerList(data, orgRef);
    },
  });

  // Every officer act settles the list (the header says why it is the mutation cache's success).
  useEffect(
    () =>
      queryClient.getMutationCache().subscribe((event) => {
        if (event.type === 'updated' && event.action.type === 'success') {
          void queryClient.invalidateQueries({ queryKey: boardToDoKey(orgRef) });
        }
      }),
    [queryClient, orgRef],
  );

  // The person's own board seat, from the roster the console already reads.
  const seatsQuery = useQuery({
    ...operations.officerListBoardMembersOptions({ path: { org_ref: orgRef } }),
    enabled: account !== null,
    select: extractBoardMembers,
  });
  const holdsSeat = useMemo(
    () =>
      account !== null &&
      (seatsQuery.data ?? []).some(
        (seat) =>
          seat.user_id === account && seat.status === 'active' && !isCommitteeOnlySeat(seat),
      ),
    [account, seatsQuery.data],
  );

  // Something new opens the list on arrival at a Board page, never mid-page (the store's rule): the
  // page is part of what it notices, and an arrival lasts until the read has answered there.
  const page = usePathname();
  const settled = !toDoQuery.isFetching;
  const list = toDoQuery.data ?? null;
  const identities = list === null ? null : identitiesOf(list).join('\n');
  useEffect(() => {
    if (identities === null) return;
    store?.notice(
      'officer',
      account,
      identities.length === 0 ? [] : identities.split('\n'),
      page,
      settled,
    );
  }, [store, account, identities, page, settled]);

  return (
    <ToDoPanel
      surface="officer"
      account={account}
      list={list}
      failed={toDoQuery.isError}
      onRetry={() => void toDoQuery.refetch()}
      testId="todo"
      footer={
        holdsSeat ? (
          <p className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary">
            {w.alsoOnTheBoard}{' '}
            <Link
              to={BOARD_DIRECTOR_PAGES.meetings}
              // The board page's own address, `?org=<org_ref>` (`director/director-board.ts`), spelled
              // here: an officer surface imports nothing of the director's (the capacity rail).
              search={{ org: orgRef }}
              className="font-w600 text-brand-700 outline-none hover:underline focus-visible:underline"
              data-testid="todo-own-board-page"
            >
              {w.openBoardPage}
            </Link>
          </p>
        ) : undefined
      }
    />
  );
}
